import { useState } from "react";

import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent } from "../ui/dialog";
import { RadioGroup } from "../ui/radio-group";
import { ReviewStarPicker } from "./ReviewStarPicker";

/**
 * ReviewCloseUpgradeModal — standalone review-ask modal (M11-A, plan
 * 320/322) presented after a buyer taps "closed" on `CheckinCard`.
 *
 * Pure presentational piece: no data fetching, no wiring to the
 * checkin/reviews API. `congratsHeadline`/`congratsSubtext` are props (not
 * imported constants) so a later step can compose this directly after
 * `CheckinCard`'s existing `CHECKIN_CLOSED_THANKS_HEADLINE` /
 * `CHECKIN_CLOSED_THANKS_SUBTEXT` state without this component owning that
 * copy.
 *
 * Render order (first to last): congrats headline -> congrats subtext ->
 * review prompt -> required 1-5 star picker -> optional free text ->
 * incentive-honored chip (Yes/Partially/No, only when `incentiveLabel` is
 * supplied) -> Submit.
 *
 * Submit is disabled until a star is picked. The Dialog's own dismiss
 * affordance (the header close control) calls `onClose` only — it never
 * calls `onSubmit`, and dismissing never gates or blocks a later submit
 * (no cross-render "already dismissed" state is tracked).
 */

export type IncentiveHonored = "yes" | "partially" | "no";

export interface ReviewCloseUpgradeSubmission {
  stars: number;
  body: string;
  incentiveHonored?: IncentiveHonored;
  /** True only when `confirmCloseRequired` was set and the buyer ticked it. */
  confirmClosed?: boolean;
}

export interface ReviewCloseUpgradeModalProps {
  /** Whether the dialog is open. Managed by the caller. */
  open: boolean;
  /** Agent's display name — drives the review prompt copy. */
  agentName: string;
  /** Congrats headline, rendered first. Pass `CHECKIN_CLOSED_THANKS_HEADLINE`
   *  when composing this right after CheckinCard's closed-thanks state. */
  congratsHeadline: string;
  /** Congrats subtext, rendered second. Pass
   *  `CHECKIN_CLOSED_THANKS_SUBTEXT` for the same composition. */
  congratsSubtext: string;
  /** Present only when an incentive was in play for this connection — gates
   *  the Yes/Partially/No "was it honored" chip. Absent renders no chip. */
  incentiveLabel?: string;
  /** The buyer's existing worked_together answer. The closed stage is
   *  answered once: the buyer confirms or changes these, at this moment,
   *  and never again (reviews are not editable). Read on mount only. */
  initialStars?: number;
  initialBody?: string;
  /** Set when the server's only close evidence is the agent's CRM close
   *  (`close_confirm_required`): Submit stays disabled until the buyer
   *  confirms the close themselves. That confirmation is the D11 tap. */
  confirmCloseRequired?: boolean;
  /** Called on Submit with the buyer's answers. Never called on dismiss. */
  onSubmit: (result: ReviewCloseUpgradeSubmission) => void;
  /** Called when the dialog is dismissed (header close, escape, overlay
   *  click). Never calls `onSubmit`. */
  onClose: () => void;
}

/**
 * Review-prompt copy — character-for-character, `{agentName}` interpolated.
 */
export function REVIEW_CLOSE_UPGRADE_PROMPT(agentName: string): string {
  return `While it's fresh — how was ${agentName}?`;
}

const INCENTIVE_OPTIONS: Array<{ value: IncentiveHonored; label: string }> = [
  { value: "yes", label: "Yes" },
  { value: "partially", label: "Partially" },
  { value: "no", label: "No" },
];

export function ReviewCloseUpgradeModal({
  open,
  agentName,
  congratsHeadline,
  congratsSubtext,
  incentiveLabel,
  initialStars,
  initialBody,
  confirmCloseRequired = false,
  onSubmit,
  onClose,
}: ReviewCloseUpgradeModalProps) {
  const [stars, setStars] = useState(initialStars ?? 0);
  const [body, setBody] = useState(initialBody ?? "");
  const [incentiveHonored, setIncentiveHonored] = useState<
    IncentiveHonored | undefined
  >(undefined);
  const [closeConfirmed, setCloseConfirmed] = useState(false);
  const canSubmit = stars > 0 && (!confirmCloseRequired || closeConfirmed);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      stars,
      body,
      incentiveHonored,
      ...(confirmCloseRequired ? { confirmClosed: true } : {}),
    });
  };

  return (
    <Dialog
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      open={open}
    >
      <DialogContent title="Leave a review">
        <div className="space-y-4 py-2" data-testid="review-upgrade-body">
          <p
            className="text-lg font-bold text-foreground"
            data-testid="review-upgrade-congrats-headline"
          >
            {congratsHeadline}
          </p>
          <p
            className="text-sm text-muted-foreground"
            data-testid="review-upgrade-congrats-subtext"
          >
            {congratsSubtext}
          </p>
          <p
            className="text-sm font-semibold text-foreground"
            data-testid="review-upgrade-prompt"
          >
            {REVIEW_CLOSE_UPGRADE_PROMPT(agentName)}
          </p>

          <ReviewStarPicker
            onChange={setStars}
            testId="review-upgrade-stars"
            value={stars}
          />

          <textarea
            aria-label="Review text (optional)"
            className="w-full rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            data-testid="review-upgrade-body-input"
            onChange={(e) => setBody(e.target.value)}
            placeholder="Your review... (optional)"
            rows={3}
            value={body}
          />

          {incentiveLabel && (
            <div data-testid="review-upgrade-incentive-group">
              <p className="text-sm text-foreground">
                Was {incentiveLabel} honored?
              </p>
              <RadioGroup
                className="mt-2 flex gap-2 space-y-0"
                onValueChange={(v) =>
                  setIncentiveHonored(v as IncentiveHonored)
                }
                options={INCENTIVE_OPTIONS}
                value={incentiveHonored}
              />
            </div>
          )}

          {confirmCloseRequired && (
            <div data-testid="review-upgrade-confirm-closed">
              <Checkbox
                checked={closeConfirmed}
                label={`Yes — we closed on a home with ${agentName}`}
                onCheckedChange={setCloseConfirmed}
              />
            </div>
          )}

          <Button
            className="w-full"
            data-testid="review-upgrade-submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Submit
          </Button>
          <Button
            className="w-full"
            data-testid="review-upgrade-dismiss"
            onClick={onClose}
            variant="ghost"
          >
            Dismiss
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
